import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * Theme wrapper around the `apartment-sync-map` block. Mirrors
 * ApartmentSyncSection (floor plans) so the Interactive Map page owns a
 * real map slot instead of the misplaced FeeGuide iframe.
 *
 * Also stabilizes load UX: reserved height, a soft placeholder until RRAC
 * paints, and cleanup of orphaned body-level RRAC loaders left behind by
 * SPA navigations from other ApartmentSync pages.
 */
export function ApartmentSyncMapSection() {
  const blockSlot = useBlockSlot('apartmentsyncmapsection.block', {
    defaultValue: 'apartment-sync-map',
  });

  const syncSectionId = blockSlot?.sectionId ?? 'apartmentsyncmapsection.block';
  const javascriptId = useContentToken(`${syncSectionId}.javascript_id`, {
    defaultField: 'custom_apartmentsync_javascript_id',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  // Drop orphaned RRAC shells/loaders that leak onto <body> across SPA nav.
  useEffect(() => {
    const selectors = [
      'body > .rrac_loader_container',
      'body > .loadingImage',
      'body > .rrac-popup',
      'body > .rrac_modal_container',
    ];
    for (const sel of selectors) {
      document.querySelectorAll(sel).forEach((el) => el.remove());
    }
  }, []);

  // Hide our placeholder once the map container has real RRAC content
  // (or the vendor loader has finished). Fail open after a timeout so a
  // stalled third-party script never leaves the page blank.
  useEffect(() => {
    if (!javascriptId) {
      setMapReady(true);
      return;
    }

    const root = containerRef.current;
    if (!root) return;

    const isReady = () => {
      const view = root.querySelector('.rrac_interactive_leasing_view');
      if (!view) return false;
      const stillLoading = view.querySelector('.rrac_loader_container, .loadingImage');
      if (stillLoading) return false;
      return view.childElementCount > 0;
    };

    if (isReady()) {
      setMapReady(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (isReady()) setMapReady(true);
    });
    observer.observe(root, { childList: true, subtree: true });

    const failOpen = window.setTimeout(() => setMapReady(true), 15000);
    return () => {
      observer.disconnect();
      window.clearTimeout(failOpen);
    };
  }, [javascriptId, blockSlot?.sectionId]);

  return (
    <section className="apartment-sync-map-section bg-background">
      <EditableToken
        path="apartmentsyncmapsection.block"
        as="div"
        className="apartment-sync-map-section__inner"
      >
        <div ref={containerRef} className="apartment-sync-map-section__stage">
          {!mapReady && javascriptId ? (
            <div className="apartment-sync-map-section__placeholder" aria-live="polite">
              <span className="apartment-sync-map-section__pulse">Loading interactive map…</span>
            </div>
          ) : null}

          {!javascriptId ? (
            <div className="apartment-sync-map-section__empty">
              Interactive map is not configured for this property yet.
            </div>
          ) : blockSlot ? (
            <SectionContextProvider sectionId={blockSlot.sectionId}>
              <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
            </SectionContextProvider>
          ) : (
            <div className="apartment-sync-map-section__empty">No block selected</div>
          )}
        </div>
      </EditableToken>

      <style>{`
        .apartment-sync-map-section {
          width: 100%;
          padding: 1.5rem 0 2.5rem;
        }
        .apartment-sync-map-section__inner {
          width: 100%;
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .apartment-sync-map-section__inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .apartment-sync-map-section__inner { padding: 0 2rem; }
        }
        .apartment-sync-map-section__stage {
          position: relative;
          width: 100%;
          min-height: 560px;
          background: #ffffff;
          border-radius: 6px;
          overflow: hidden;
        }
        .apartment-sync-map-section__placeholder {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          pointer-events: none;
        }
        .apartment-sync-map-section__pulse {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
          animation: asm-pulse 1.4s ease-in-out infinite;
        }
        .apartment-sync-map-section__empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 4rem 1rem;
        }
        .apartment-sync-map-section .rrac_interactive_leasing_view {
          min-height: 560px;
        }
        @keyframes asm-pulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .apartment-sync-map-section__pulse { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncMapSection',
  displayName: 'Apartment Sync Map Section',
  tokens: {
    'apartmentsyncmapsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ApartmentSyncMapSection;
